// context/AdviserStatusContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { firestore } from "../backend/firebaseconfig";
import { useUserRole } from "../context/UserRoleContext";
import { useUserEmail } from "../context/UserEmailContext";

const AdviserStatusContext = createContext({
  adviserStatus: [],
  setAdviserStatus: () => {},
  refreshAdviserStatus: async () => {},
  loading: false,
  error: null,
});

export const AdviserStatusProvider = ({ children }) => {
  const { userRole } = useUserRole();
  const { userEmail, setUserEmail } = useUserEmail();
  const [adviserStatus, setAdviserStatus] = useState(() => {
    try {
      const stored = localStorage.getItem("adviserStatus");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refreshAdviserStatus = useMemo(
    () => async () => {
      if (userRole !== "Advisers" || !userEmail) return;
      setLoading(true);
      setError(null);
      try {
        const advisersCol = collection(
          firestore,
          "Authorized Members/Advisers/Data"
        );
        const adviserQuery = query(
          advisersCol,
          where("email", "==", userEmail)
        );
        const snap = await getDocs(adviserQuery);

        if (!snap.empty) {
          const data = snap.docs[0].data();
          const status = Array.isArray(data?.status) ? data.status : [];
          setAdviserStatus(status);
        } else {
          setAdviserStatus([]); // no record found
        }
      } catch (e) {
        // console.error("Error fetching adviser status:", e);
        setError(e);
      } finally {
        setLoading(false);
      }
    },
    [userRole, userEmail]
  );

  // auto-fetch when role/email become available or change
  useEffect(() => {
    refreshAdviserStatus();
  }, [refreshAdviserStatus]);

  // keep localStorage in sync
  useEffect(() => {
    try {
      if (adviserStatus?.length) {
        localStorage.setItem("adviserStatus", JSON.stringify(adviserStatus));
      } else {
        localStorage.removeItem("adviserStatus");
      }
    } catch {}
  }, [adviserStatus]);

  return (
    <AdviserStatusContext.Provider
      value={{
        adviserStatus,
        setAdviserStatus,
        refreshAdviserStatus,
        loading,
        error,
      }}
    >
      {children}
    </AdviserStatusContext.Provider>
  );
};

export const useAdviserStatus = () => {
  const ctx = useContext(AdviserStatusContext);
  if (!ctx) {
    // throw new Error(
    //   "useAdviserStatus must be used within an AdviserStatusProvider"
    // );
  }
  return ctx;
};
